import { useRef } from 'react';
import { useField, useForm, SubmitResult } from '@shopify/react-form';
import { LaborCostSettings, SaveLaborCostSettings } from '@sdk/labor/types';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import getErrorMessages from 'sling-sdk/src/utils/getErrorMessages';
import useSlingApi from '@sdk/api/useSlingApi';

import { decorateSubmit } from '@web/forms/utils/decorateSubmit';
import { FormSubmitStatuses } from '@web/forms/types';

import { laborSettingsParser } from '../../../../settings/components/laborCost/helpers/laborSettingsParser';
type Props = {
  laborCost: LaborCostSettings;
  saveLaborCostSettingsInAngular: SaveLaborCostSettings;
};

export const useLaborCostForm = ({ laborCost, saveLaborCostSettingsInAngular }: Props) => {
  const api = useSlingApi();
  const submitPromise = useRef<Promise<SubmitResult>>();

  const {
    fields,
    submit: formSubmit,
    dirty,
    reset,
    submitErrors,
  } = useForm({
    fields: {
      overtimeEnabled: useField(laborCost?.overtimeEnabled),
      overtimeWeekThreshold: useField({
        value: laborCost?.overtimeWeekThreshold,
        validates: (value) => {
          if (
            !validateNumber(value, {
              [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
            })
          ) {
            return 'overtimeWeekThreshold invalid';
          }
        },
      }),
      overtimeWeekMultiplier: useField({
        value: laborCost?.overtimeWeekMultiplier,
        validates: (value) => {
          if (
            !validateNumber(value, {
              [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
            })
          ) {
            return 'overtimeWeekMultiplier invalid';
          }
        },
      }),
    },
    onSubmit: async (fieldValues: Partial<LaborCostSettings>) => {
      try {
        const payload = laborSettingsParser({ ...laborCost, ...fieldValues });

        await api.laborCost.saveLaborSettings(payload);
        await saveLaborCostSettingsInAngular({ settings: payload });

        submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
        return submitPromise.current;
      } catch (error) {
        const [errorMsg] = getErrorMessages(error);

        submitPromise.current = Promise.resolve({
          status: FormSubmitStatuses.fail,
          errors: [{ message: errorMsg }],
        });
        return submitPromise.current;
      }
    },
  });

  const submit = decorateSubmit(formSubmit, submitPromise);

  return { fields, submit, dirty, reset, submitErrors };
};
